/*!
 * SkyPlus Document Type Configuration
 * Location: app/src/app/components/skyplus/indexing/document-types.config.ts
 * 
 * This configuration file defines all document types available for indexing.
 * To add a new document type:
 * 1. Add the document type definition to DOCUMENT_TYPES array
 * 2. Define all fields with their Alfresco property names
 * 3. Specify which field(s) can be used for folder creation
 * 4. Specify which field(s) can be used for SUBFOLDER creation (NEW)
 * 
 * The configuration is designed to be easily extended without changing component code.
 */

// Field types for the metadata form
export type FieldType = 'text' | 'date' | 'number' | 'select' | 'textarea';

// Individual field definition
export interface DocumentField {
  /** Alfresco property name (e.g., 'zim:memberCRMMemberId') */
  propertyName: string;
  
  /** Display label shown in the form */
  label: string;
  
  /** Field input type */
  type: FieldType;
  
  /** Is this field required? */
  mandatory: boolean;
  
  /** Can this field be used to create the MAIN folder? */
  canCreateFolder?: boolean;
  
  /** Can this field be used to create a SUBFOLDER within the main folder? (NEW) */
  canCreateSubfolder?: boolean;
  
  /** 
   * For select fields with canCreateSubfolder: use option LABEL as folder name instead of value?
   * If true: "Invoice" folder (from label)
   * If false: "INV" folder (from value)
   * Default: true (use label for better readability)
   */
  subfolderUseLabel?: boolean;
  
  /** Placeholder text for the input */
  placeholder?: string;

  /**
   * Hot-folder capture: the field NAME used in the sidecar XML
   * (<field name="..."/>) that maps to this property. When a scanned PDF
   * arrives with a sidecar, the capture service copies that field's value
   * into this property. Defaults to the field label when omitted.
   */
  xmlFieldName?: string;
  
  /** Default value */
  defaultValue?: string;
  
  /** For 'select' type - list of options */
  options?: { value: string; label: string }[];
  
  /** Validation pattern (regex) */
  pattern?: string;
  
  /** Max length for text fields */
  maxLength?: number;
  
  /** Auto-populate with current user ID */
  autoPopulateUserId?: boolean;
  
  /** Auto-populate with current date */
  autoPopulateDate?: boolean;

  /**
   * Strike A — show this field on the capture (indexing) form. Undefined is
   * treated as true so existing static types (which omit it) are unchanged.
   */
  capture?: boolean;

  /**
   * Strike A — expose this field in advanced / contextual search. Undefined is
   * treated as true so existing static types behave exactly as before.
   */
  search?: boolean;

  /**
   * Show this aspect field as a COLUMN in the document library list header (like the
   * search flag, but for the table). Undefined/false = not a column. When a folder's
   * document type has any column-flagged fields, the list shows those columns (reading
   * each node's aspect property); otherwise it falls back to the standard columns.
   */
  column?: boolean;

  /**
   * F3 — hidden from the capture/memo wizard AND the filing form, but still offered
   * as a dashboard / step-list column (e.g. a folio file reference bridged via
   * systemSource). Undefined/false = a normal field.
   */
  hidden?: boolean;

  /**
   * Strike E — config-driven ordered folder placement. When enabled, this
   * field's value (or option label, for selects, when useLabel) becomes a
   * sub-folder, created in ascending `order`. Supersedes the legacy
   * canCreateFolder / canCreateSubfolder two-level fields for registry types.
   */
  folderSegment?: { enabled?: boolean; order?: number; useLabel?: boolean };
}

// Document type definition
export interface DocumentTypeConfig {
  /** Unique identifier */
  id: string;

  /** Round 7 — backend type key (same value as id for registry types). */
  typeKey?: string;

  /** Round 7 — folder rule engine config, carried verbatim from the
   *  registry so capture flows can detect and apply rules. */
  folderRules?: any;
  
  /** Display name */
  name: string;
  
  /** Description shown in the UI */
  description: string;
  
  /** Alfresco node type (e.g., 'zim:memberCRMDocumentType') */
  nodeType: string;
  
  /** Alfresco aspect name (e.g., 'zim:memberDocAspects'). Legacy single-aspect. */
  aspectName: string;

  /**
   * Strike D — full list of Alfresco aspects to stamp on upload so that
   * indexed properties become searchable. Populated for runtime (registry)
   * types; static types fall back to [aspectName]. Optional + additive:
   * existing static entries that omit it are unaffected.
   */
  aspectNames?: string[];
  
  /** Icon for the UI (Material icon name) */
  icon: string;
  
  /** Color for the UI */
  color: string;
  
  /** Base folder path in Alfresco (e.g., '/ZIMASADMS/MEMBERS') */
  baseFolderPath?: string;
  
  /** All fields for this document type */
  fields: DocumentField[];
  
  /** Is this document type enabled? */
  enabled: boolean;
  
  /** Sort order in the dropdown */
  sortOrder: number;
}

// ============================================================================
// DOCUMENT TYPE DEFINITIONS
// ============================================================================

export const DOCUMENT_TYPES: DocumentTypeConfig[] = [
  
  // -------------------------------------------------------------------------
  // MEMBER DOCUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'member',
    name: 'Member Documents',
    description: 'Documents related to member Records',
    nodeType: 'skyplus:newMemberDocType',
    aspectName: 'skyplus:newmemberDocAspects',
    icon: 'person',
    color: '#4A90D9',
    baseFolderPath: '/Bandari-ECM/MEMBER FILES',
    enabled: true,
    sortOrder: 1,
    fields: [
      {
        propertyName: 'skyplus:newMemberNo',
        label: 'Member No',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter member No',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:newMemberName',
        label: 'Member Name',
        type: 'text',
        mandatory: true,
        placeholder: 'Enter member full name',
        maxLength: 200
      },
      {
        propertyName: 'skyplus:memberIdNo',
        label: 'Id No',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter id no',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:memberMobile',
        label: 'Mobile',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter mobile number',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:memberLoanNo',
        label: 'Loan No',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter mobile number',
        maxLength: 50
      },	  
	   {
        propertyName: 'skyplus:newMemberDocCategory',
        label: 'Document Category',
        type: 'select',
        mandatory: true,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'KYC', label: 'KYC' },
          { value: 'ECHANNELS', label: 'E-Channels' },
          { value: 'CORRESPONDENCE', label: 'Correspondence' },
          { value: 'CONFIDENTIAL FILE', label: 'Confidential File' },
          { value: 'TRANSACTIONAL', label: 'Transactional Documents' },
          { value: 'MEMBER EXIT', label: 'Member Exit' },
          { value: 'PENSION CLEARANCE', label: 'Pension Clearance' },
          { value: 'Loans', label: 'Loans' }
        ]
      },
      {
        propertyName: 'skyplus:memberDocType',
        label: 'Document Type',
        type: 'select',
        mandatory: true,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'ACC APPLICATION', label: 'Account Opening Form' },
          { value: 'APPLICATION', label: 'Member Application Form' },
          { value: 'ID', label: 'ID Copy' },
          { value: 'ATM', label: 'ATM Card Application' },
          { value: 'ATM COMPLIANT', label: 'ATM Compliant Form' },
          { value: 'ATM PIN', label: 'ATM Pin Reset Form' },
          { value: 'DAU', label: 'DAU Children Acc Opening Form' },
          { value: 'CLEARANCE', label: 'Clearance Approval Letter' },
          { value: 'DORMANT', label: 'Dormant Activation Form' },
          { value: 'EFT', label: 'EFT Standing Order Form' },
          { value: 'EMPLOYER', label: 'Employer Clearance Letter' },
          { value: 'FIXED', label: 'Fixed Deposit Form' },
          { value: 'FOSA DEPOSIT', label: 'Fosa Deposit Adjustment Form' },
          { value: 'DEPOSIT WITHDRAWAL', label: 'Fosa Deposit Withdrawal Form' },
          { value: 'FOSA', label: 'Fosa Membership Application' },
          { value: 'FUNDS', label: 'Funds Transfer RTGS and EFT' },
          { value: 'KRA', label: 'KRA Pin' },
          { value: 'LOAN', label: 'Loan Clearance Report' },
          { value: 'LPO', label: 'LPO Requirement Form' },
          { value: 'MEMBER ACC', label: 'Member Account Statement' },
          { value: 'UPDATE', label: 'Membership Information Update' },
          { value: 'MOBILE', label: 'Mobile Banking Form' },
          { value: 'NOK', label: 'Next of Kin Card' },
          { value: 'NOMINEE', label: 'Nominee Card' },
          { value: 'PENSION', label: 'Pension Clearance Form' },
          { value: 'RETIREMENT', label: 'Retirement Notice' },
          { value: 'SALARY', label: 'Salary Transfer Form' },
          { value: 'SHARE CAPITAL', label: 'Share Capital Adjustment Form' },
          { value: 'SHARE DEPOSIT', label: 'Share Deposit Adjustment Form' },
          { value: 'STANDING', label: 'Standing Order Form' },
          { value: 'LOANS', label: 'Loans Application Form' },
          { value: 'GUARANTOR', label: 'Guarantor Substitution Form' },
          { value: 'OTHER', label: 'Other' }
        ]
      },
      {
        propertyName: 'skyplus:newMemberuserName',
        label: 'Username',
        type: 'text',
        mandatory: true,
        autoPopulateUserId: true
      }
    ]
  },

  // -------------------------------------------------------------------------
  // ADMIN BOARD DOCUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'board',
    name: 'Board Documents',
    description: 'Admin Board documents',
    nodeType: 'skyplus:adminBoardDocType',
    aspectName: 'skyplus:adminBoardDocAspects',
    icon: 'description',
    color: '#28a745',
    baseFolderPath: '/Bandari-ECM/ADMINISTRATION SECRETARY',
    enabled: true,
    sortOrder: 2,
    fields: [
      {
        propertyName: 'skyplus:adminBoardYear',
        label: 'Year',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter year',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:adminBoardMonth',
        label: 'Month',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter month',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:adminBoardDocType',
        label: 'Document Type',
        type: 'select',
        mandatory: true,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'AGENDAS', label: 'Agendas' },
          { value: 'BOARD MEETING MINUTES', label: 'Board Meeting Minutes' },
          { value: 'BOARD RESOLUTIONS', label: 'Board Resolutions' },
          { value: 'REPORTS', label: 'Reports Presented to Board' },
          { value: 'OTHER', label: 'Other' }
        ]
      },
      {
        propertyName: 'skyplus:adminBoarduserName',
        label: 'Username',
        type: 'text',
        mandatory: true,
        autoPopulateUserId: true
      }
    ]
  },

  // -------------------------------------------------------------------------
  // SERVICE PROVIDER DOCUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'provider',
    name: 'Service Provider Documents',
    description: 'Service Provider documents',
    nodeType: 'skyplus:serviceProviderDocType',
    aspectName: 'skyplus:serviceProviderDocAspects',
    icon: 'source',
    color: '#00BCD4',
    baseFolderPath: '/Bandari-ECM/ADMINISTRATION SECRETARY',
    enabled: true,
    sortOrder: 3,
    fields: [
      {
        propertyName: 'skyplus:serviceProviderVendorId',
        label: 'Vendor ID',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter vendor id',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:serviceProviderVendorName',
        label: 'Vendor Name',
        type: 'text',
        mandatory: true,
        placeholder: 'Enter vendor name',
        maxLength: 200
      },
      {
        propertyName: 'skyplus:serviceProviderContractNo',
        label: 'Contract No',
        type: 'text',
        mandatory: true,
        canCreateSubfolder: true,
        placeholder: 'Enter contract no',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:serviceProviderServiceCategory',
        label: 'Service Category',
        type: 'text',
        mandatory: true,
        placeholder: 'Enter service category'
      },
      {
        propertyName: 'skyplus:serviceProviderDocType',
        label: 'Document Type',
        type: 'select',
        mandatory: true,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'CONTRACT', label: 'Contract' },
          { value: 'CONTRACT AGREEMENT', label: 'Contract Agreement' },
          { value: 'CONTRACT RENEWAL', label: 'Contract Renewal' },
          { value: 'PERFORMANCE REPORT', label: 'Performance Report' },
          { value: 'SLA', label: 'Service Level Agreement' },
          { value: 'VENDOR REGISTRATION', label: 'Vendor Registration' },
          { value: 'OTHER', label: 'Other' }
        ]
      },
      {
        propertyName: 'skyplus:serviceProvideruserName',
        label: 'Username',
        type: 'text',
        mandatory: true,
        autoPopulateUserId: true
      }
    ]
  },

  // -------------------------------------------------------------------------
  // STAFF DOCUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'staff',
    name: 'Staff Documents',
    description: 'Staff documents',
    nodeType: 'skyplus:hrStaffFileDocType',
    aspectName: 'skyplus:hrStaffFileDocAspects',
    icon: 'group',
    color: '#dc3545',
    baseFolderPath: '/Bandari-ECM/HUMAN RESOURCE',
    enabled: true,
    sortOrder: 4,
    fields: [
      {
        propertyName: 'skyplus:hrStaffStaffNo',
        label: 'Staff Number',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter staff number',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:hrStaffStaffName',
        label: 'Staff Name',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter staff name',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:hrStaffIdNo',
        label: 'Staff Id No',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter staff id no',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:hrStaffPhoneNo',
        label: 'Staff Phone No',
        type: 'text',
        mandatory: true,
        placeholder: 'Enter staff phone no',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:hrStaffDepartment',
        label: 'Staff Department',
        type: 'text',
        mandatory: false,
        placeholder: 'Enter staff department',
        maxLength: 200
      },
      {
        propertyName: 'skyplus:hrStaffFileDocType',
        label: 'Document Type',
        type: 'select',
        mandatory: true,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'ACADEMIC CERTIICATE', label: 'Academic Certificates' },
          { value: 'APPOINTMENT LETTER', label: 'Appointment Letter' },
          { value: 'DISCIPLINARY RECORDS', label: 'Disciplinary Records' },
          { value: 'EMPLOYMENT CONTRACT', label: 'Employment Contract' },
          { value: 'LEAVE RECORDS', label: 'Leave Records' },
          { value: 'ID', label: 'National ID Copy' },
          { value: 'APPRAISALS', label: 'Performance Appraisals' },
          { value: 'OTHER', label: 'Other' }
        ]
      },
      {
        propertyName: 'skyplus:hrStaffFileuserName',
        label: 'Username',
        type: 'text',
        mandatory: true,
        autoPopulateUserId: true
      }
    ]
  },

  // -------------------------------------------------------------------------
  // TRAINING DOCUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'training',
    name: 'Training Documents',
    description: 'Training documents',
    nodeType: 'skyplus:underwritingDocumentType',
    aspectName: 'skyplus:hrStafftrainDocAspects',
    icon: 'menu_book',
    color: '#17a2b8',
    baseFolderPath: '/Bandari-ECM/ADMINISTRATION SECRETARY',
    enabled: true,
    sortOrder: 5,
    fields: [
      {
        propertyName: 'skyplus:hrStafftrainSubject',
        label: 'Subject',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter subject',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:hrStafftrainDate',
        label: 'Date',
        type: 'text',
        mandatory: true,
        canCreateFolder: true,
        placeholder: 'Enter date -format dd-MM-yyyy',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:hrStafftrainDocType',
        label: 'Document Type',
        type: 'select',
        mandatory: true,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'TRAINING AGENDA', label: 'Training Agenda' },
          { value: 'TRAINING APPROVAL', label: 'Training Approval' },
          { value: 'TRAINING MATERIALS', label: 'Training Materials' },
          { value: 'TRAINING PROPOSAL', label: 'Training Proposal' },
          { value: 'TRAINING REPORT', label: 'Training Report' },
          { value: 'OTHER', label: 'Other' }
        ]
      },
      {
        propertyName: 'skyplus:hrStafftrainuserName',
        label: 'Username',
        type: 'text',
        mandatory: true,
        autoPopulateUserId: true
      }
    ]
  },


  // -------------------------------------------------------------------------
  // RECONCILIATION DOCUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'reconciliation',
    name: 'Reconciliation Documents',
    description: 'Reconciliation documents',
    nodeType: 'skyplus:reconnDocType',
    aspectName: 'skyplus:reconnDocAspects',
    icon: 'fact_check',
    color: '#fdcb6e',
    baseFolderPath: '/Bandari-ECM/ACCOUNTS',
    enabled: true,
    sortOrder: 6,
    fields: [
      {
        propertyName: 'skyplus:reconnBank',
        label: 'Bank',
        type: 'text',
        mandatory: false,
        canCreateFolder: true,
        placeholder: 'Enter bank name',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:reconnYear',
        label: 'Year',
        type: 'text',
        mandatory: false,
        canCreateFolder: true,
        placeholder: 'Enter year',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:reconnMonth',
        label: 'Month',
        type: 'text',
        mandatory: false,
        canCreateFolder: true,
        placeholder: 'Enter month',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:reconnDocType',
        label: 'Document Type',
        type: 'select',
        mandatory: false,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'BANK STATEMENT', label: 'Bank Statement' },
          { value: 'BANK RECONCILIATION', label: 'Bank Reconciliation Report' },
          { value: 'OTHER', label: 'Other' }
        ]
      },
      {
        propertyName: 'skyplus:reconnuserName',
        label: 'Username',
        type: 'text',
        mandatory: false,
        autoPopulateUserId: true
      }
    ]
  },
  
  
  // -------------------------------------------------------------------------
  // PETTY DOCUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'pettycash',
    name: 'Petty Cash Documents',
    description: 'Petty Cash documents',
    nodeType: 'skyplus:pettyCashDocType',
    aspectName: 'skyplus:pettyCashDocAspects',
    icon: 'fact_check',
    color: '#fdcb6e',
    baseFolderPath: '/Bandari-ECM/ACCOUNTS',
    enabled: true,
    sortOrder: 7,
    fields: [
      {
        propertyName: 'skyplus:pettyCashTransactionNo',
        label: 'Cash',
        type: 'text',
        mandatory: false,
        canCreateFolder: true,
        placeholder: 'Enter transaction number',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:pettyCashCustomerNo',
        label: 'Client',
        type: 'text',
        mandatory: false,
        canCreateFolder: true,
        placeholder: 'Enter customer number',
        maxLength: 50
      },
	  {
        propertyName: 'skyplus:pettyCashDocType',
        label: 'Document Type',
        type: 'select',
        mandatory: false,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'RECEIPTS', label: 'Receipts' },
          { value: 'ATTACHMENTS', label: 'Attachments' },
          { value: 'OTHER', label: 'Other' }
        ]
      },
      {
        propertyName: 'skyplus:pettyCashuserName',
        label: 'Username',
        type: 'text',
        mandatory: false,
        autoPopulateUserId: true
      }
    ]
  },  

  // -------------------------------------------------------------------------
  // TRAVEL MANAGEMENT DOCUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'travelmanagement',
    name: 'Travel Management Documents',
    description: 'Travel Management documents',
    nodeType: 'skyplus:travelManagementDocType',
    aspectName: 'skyplus:travelManagementDocAspects',
    icon: 'fact_check',
    color: '#fdcb6e',
    baseFolderPath: '/Bandari-ECM/ACCOUNTS',
    enabled: true,
    sortOrder: 8,
    fields: [
      {
        propertyName: 'skyplus:travelManagementTravelManagementNo',
        label: 'Travel',
        type: 'text',
        mandatory: false,
        canCreateFolder: true,
        placeholder: 'Enter travel management number',
        maxLength: 50
      },
      {
        propertyName: 'skyplus:travelManagementExpenses',
        label: 'Client',
        type: 'text',
        mandatory: false,
        canCreateFolder: true,
        placeholder: 'Enter  expenses',
        maxLength: 50
      },
	  {
        propertyName: 'skyplus:travelManagementDocType',
        label: 'Document Type',
        type: 'select',
        mandatory: false,
        canCreateSubfolder: true,
        subfolderUseLabel: true,
        options: [
          { value: 'RECEIPTS', label: 'Receipts' },
          { value: 'ATTACHMENTS', label: 'Attachments' },
          { value: 'OTHER', label: 'Other' }
        ]
      },
      {
        propertyName: 'skyplus:travelManagementuserName',
        label: 'Username',
        type: 'text',
        mandatory: false,
        autoPopulateUserId: true
      }
    ]
  }

];  


// ============================================================================
// RUNTIME OVERRIDE (Strike D)
// ----------------------------------------------------------------------------
// When the document-type registry (GET /api/wf/document-types) has types, the
// WfDocumentTypeRegistryService maps them into DocumentTypeConfig[] and calls
// setRuntimeDocumentTypes(). All helper functions below then transparently read
// the runtime set instead of the static DOCUMENT_TYPES, so every existing call
// site becomes registry-driven WITHOUT any change. If the registry is empty or
// unreachable, the runtime set is left null and the static array is used — so
// capture/search never breaks.
// ============================================================================

let RUNTIME_DOCUMENT_TYPES: DocumentTypeConfig[] | null = null;

/** Install registry-sourced types as the active set. Pass [] or null to clear. */
export function setRuntimeDocumentTypes(types: DocumentTypeConfig[] | null): void {
  RUNTIME_DOCUMENT_TYPES = (types && types.length) ? types : null;
}

/** Revert to the built-in static DOCUMENT_TYPES. */
export function clearRuntimeDocumentTypes(): void {
  RUNTIME_DOCUMENT_TYPES = null;
}

/** The active type set: runtime (registry) if installed, else the static array. */
export function activeDocumentTypes(): DocumentTypeConfig[] {
  return RUNTIME_DOCUMENT_TYPES ?? DOCUMENT_TYPES;
}


// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all enabled document types sorted by sortOrder
 */
export function getEnabledDocumentTypes(): DocumentTypeConfig[] {
  return activeDocumentTypes()
    .filter(dt => dt.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Get a document type by its ID
 */
export function getDocumentTypeById(id: string): DocumentTypeConfig | undefined {
  return activeDocumentTypes().find(dt => dt.id === id);
}

/**
 * Get a document type by its Alfresco node type
 */
export function getDocumentTypeByNodeType(nodeType: string): DocumentTypeConfig | undefined {
  return activeDocumentTypes().find(dt => dt.nodeType === nodeType);
}

/**
 * Get fields that can be used to create MAIN folders for a document type
 */
export function getFolderCreationFields(documentTypeId: string): DocumentField[] {
  const docType = getDocumentTypeById(documentTypeId);
  if (!docType) return [];
  return docType.fields.filter(f => f.canCreateFolder);
}

/**
 * Get fields that can be used to create SUBFOLDERS for a document type (NEW)
 */
export function getSubfolderCreationFields(documentTypeId: string): DocumentField[] {
  const docType = getDocumentTypeById(documentTypeId);
  if (!docType) return [];
  return docType.fields.filter(f => f.canCreateSubfolder);
}

/**
 * Get mandatory fields for a document type
 */
export function getMandatoryFields(documentTypeId: string): DocumentField[] {
  const docType = getDocumentTypeById(documentTypeId);
  if (!docType) return [];
  return docType.fields.filter(f => f.mandatory);
}

/**
 * Get the subfolder name from a field value (NEW)
 * For select fields, can return the label instead of value
 */
export function getSubfolderName(field: DocumentField, value: any): string {
  if (!value) return '';
  
  // For select fields, optionally use label
  if (field.type === 'select' && field.options && field.subfolderUseLabel !== false) {
    const option = field.options.find(o => o.value === value);
    if (option) {
      return option.label;
    }
  }
  
  // Default: use the value as-is
  return String(value).trim();
}

/**
 * Build the properties object for Alfresco API from form values
 */
export function buildAlfrescoProperties(
  documentTypeId: string, 
  formValues: Record<string, any>
): Record<string, any> {
  const docType = getDocumentTypeById(documentTypeId);
  if (!docType) return {};
  
  const properties: Record<string, any> = {};
  
  docType.fields.forEach(field => {
    const value = formValues[field.propertyName];
    if (value !== undefined && value !== null && value !== '') {
      properties[field.propertyName] = value;
    }
  });
  
  return properties;
}